import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { RenderImage } from "go-app/components/ImageRenderer/ListImageName";
import handleError from "go-app/services/errors";
import EmptyData from "go-core/components/EmptyData";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ReactComponent as LockSVG } from "../../../../../../../../images/svg/liveOrders/lock.svg";
import { apiOrganization } from "../../../../../../../../services/Api/Organization/apiOrganization";
import { ItemApi } from "../../../../../../../../services/Api/Organization/types";

interface Props {
	products: ItemApi[];
	setProductToBlock: (product?: ItemApi) => void;
	loading: boolean;
	handleUnblockProduct: (product: ItemApi) => void;
	setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
}

const LiveOrdersBlockedProductsTableModal = ({
	products,
	setProductToBlock,
	loading,
	handleUnblockProduct,
	setEditMode,
}: Props): JSX.Element => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [loadingIndex, setLoadingIndex] = useState(-1);
	const confirmation = useConfirmation();

	const handleUnblock = async (item: ItemApi, index: number) => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			setLoadingIndex(index);
			const res = await apiOrganization.unblockItem(item.id, {
				include: "category,item_group",
			});
			handleUnblockProduct(res);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoadingIndex(-1);
	};

	const handleChangeSuspension = async (item: ItemApi, index: number) => {
		if (item.suspended) {
			await handleUnblock(item, index);
		} else {
			setProductToBlock(item);
		}
	};

	const drawBlockingInformation = (product: ItemApi) => {
		if (product.suspended) {
			return (
				<span className="text-danger ms-2 text-break">
					<LockSVG style={{ marginBottom: "3px" }} />
					{product?.suspension?.until && (
						<span className="ms-1 ">
							{`${t("lib:common.word.to")} ${FormatDate(product.suspension.until)}`}
						</span>
					)}
					{product?.suspension?.reason && product.suspension.reason !== "-" && (
						<span className="text-break">{`${product?.suspension?.until ? ", " : " "}${
							product.suspension.reason
						}`}</span>
					)}
				</span>
			);
		}
	};

	const handleEditProduct = (product: ItemApi) => {
		setEditMode(true);
		setProductToBlock(product);
	};

	return (
		<table className="table table-preview live-orders-blocked-products-table">
			<thead>
				<tr>
					<th />
					<th className="action" />
				</tr>
			</thead>
			<tbody>
				{products.length > 0
					? products.map((product, index) => (
							<tr key={`product-${product.id}`}>
								<td>
									<div className="d-flex align-items-center">
										<RenderImage
											showColor
											imageLink={product?.item_group?.image_link}
											data={product}
										/>
										<div className="d-flex flex-column">
											<div className="d-flex">
												<h5
													className={`mb-0 name text-break ${
														product.suspended ? "text-danger" : ""
													}`}
													title={product.name}
												>
													{product.name}
												</h5>
											</div>
											<div className="d-flex flex-wrap">
												<span className="text-muted text-break">{product.category.name}</span>
												{drawBlockingInformation(product)}
											</div>
											{product.suspension?.order_types &&
												product.suspension.order_types.length > 0 && (
													<div className="d-flex">
														<span>
															{`${t("lib:common.word.type")}:`}
															<span className="text-muted ms-1">
																{product.suspension.order_types
																	.map((type) => t(`enums.orders.types.${type}`))
																	.join(", ")}
															</span>
														</span>
													</div>
												)}
											{product.suspension?.sources && product.suspension.sources.length > 0 && (
												<div className="d-flex">
													<span>
														{`${t("modules.live_order.field.sources.title")}:`}
														<span className="text-muted ms-1">
															{product.suspension.sources
																.map((source) => t(`enums.orders.source.${source}`))
																.join(", ")}
														</span>
													</span>
												</div>
											)}
										</div>
									</div>
								</td>
								<td className="action">
									<div>
										{product.suspended && (
											<Button
												variant="light"
												className="me-2"
												onClick={() => handleEditProduct(product)}
											>
												{t("lib:common.action.edit")}
											</Button>
										)}
										<ButtonLoading
											loading={index === loadingIndex}
											variant={product.suspended ? "success" : "danger"}
											onClick={() => handleChangeSuspension(product, index)}
										>
											{product.suspended
												? t("modules.live_order.action.unblock.title")
												: t("modules.live_order.action.block.title")}
										</ButtonLoading>
									</div>
								</td>
							</tr>
					  ))
					: !loading && <EmptyData />}
			</tbody>
		</table>
	);
};

export default LiveOrdersBlockedProductsTableModal;
